import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  Alert,
  AutoComplete,
  Button,
  Card,
  Empty,
  Form,
  Input,
  InputNumber,
  Modal,
  Select,
  Skeleton,
  Space,
  Switch,
  Tag,
  Typography,
  message,
} from "antd";
import {
  ArrowLeftOutlined,
  DeleteOutlined,
  PlusOutlined,
  SaveOutlined,
  SendOutlined,
  SolutionOutlined,
  TeamOutlined,
  ThunderboltOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { useAuth } from "contexts/AuthContext";
import { useWorkspace } from "contexts/WorkspaceContext";
import GooglePlacesTagsSelect from "common/GooglePlacesTagsSelect";
import MentionEditor from "common/MentionEditor";
import AiJobDescriptionModal from "common/AiJobDescriptionModal";
import SidePromotionSection from "components/promotions/SidePromotionSection";
import useAppConfig from "hooks/access-control/useAppConfig";
import useAiRequestUsage from "hooks/usage/useAiRequestUsage";
import paths from "routes/paths";
import { olreadiaService } from "services/olreadiaService";
import { regionSummaryFromPlaceDetails } from "utils/googlePlaces";
import "./OlreadiaFlowPages.css";

const { Paragraph, Text, Title } = Typography;
const WORK_SCHEDULES = [
  { label: "Any", value: "ANY" },
  { label: "Full-time", value: "FULL_TIME" },
  { label: "Part-time", value: "PART_TIME" },
  { label: "Contract", value: "CONTRACT" },
  { label: "Temporary", value: "TEMPORARY" },
  { label: "Internship", value: "INTERNSHIP" },
  { label: "On call", value: "ON_CALL" },
];

const EDUCATION_LEVELS = [
  { label: "Any", value: "ANY" },
  { label: "High school", value: "HIGH_SCHOOL" },
  { label: "Associate", value: "ASSOCIATE" },
  { label: "Bachelor", value: "BACHELOR" },
  { label: "Master", value: "MASTER" },
  { label: "Doctorate", value: "DOCTORATE" },
  { label: "Certificate", value: "CERTIFICATE" },
];

const ANSWER_TYPES = [
  { label: "Text", value: "TEXT" },
  { label: "Yes / No", value: "BOOLEAN" },
  { label: "Number", value: "NUMBER" },
  { label: "Date", value: "DATE" },
  { label: "Date & time", value: "DATETIME" },
  { label: "Time", value: "TIME" },
  { label: "Single choice", value: "SINGLE_CHOICE" },
  { label: "Multiple choice", value: "MULTIPLE_CHOICE" },
];

const ANSWER_VALUE_TYPES = [
  { label: "Text", value: "TEXT" },
  { label: "Long text", value: "MULTILINE_TEXT" },
  { label: "Number", value: "NUMBER" },
  { label: "URL", value: "URL" },
  { label: "Email", value: "EMAIL" },
  { label: "Phone", value: "PHONE" },
  { label: "Date", value: "DATE" },
  { label: "Date & time", value: "DATETIME" },
  { label: "Time", value: "TIME" },
  { label: "Yes / No", value: "BOOLEAN" },
];

const HIRE_MEMBER_ROLES = [
  { label: "Interviewer", value: "INTERVIEWER" },
  { label: "Recruiter", value: "RECRUITER" },
  { label: "Coordinator", value: "COORDINATOR" },
  { label: "Hiring Manager", value: "HIRING_MANAGER" },
  { label: "HR", value: "HR" },
  { label: "Other", value: "OTHER" },
];

const HIRE_MEMBER_PRIVILEGES = [
  { label: "Admin", value: "ADMIN" },
  { label: "HR", value: "HR" },
  { label: "Hire", value: "HIRE" },
  { label: "Schedule", value: "SCHEDULE" },
  { label: "Interview", value: "INTERVIEW" },
  { label: "Manage Post", value: "MANAGE_POST" },
];

const METRIC_OPTIONS = ["dollars", "shillings", "yen", "kg", "lb", "meters"];

const CHOICE_ANSWER_TYPES = new Set(["SINGLE_CHOICE", "MULTIPLE_CHOICE"]);

const today = () => new Date().toISOString().slice(0, 10);

const addMonths = (months) => {
  const date = new Date();
  date.setMonth(date.getMonth() + months);
  return date.toISOString().slice(0, 10);
};

const titleCase = (value) =>
  String(value || "")
    .replace(/[_-]/g, " ")
    .trim()
    .toLowerCase()
    .replace(/\b\w/g, (char) => char.toUpperCase());

const normalizeTagList = (values = []) =>
  (Array.isArray(values) ? values : [])
    .map((value) => String(value || "").trim())
    .filter(Boolean);

const escapeHtml = (text) =>
  String(text || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

const locationPayload = (label) => {
  const text = String(label || "").trim();
  if (!text) return null;
  const parts = text.split(",").map((part) => part.trim()).filter(Boolean);
  return {
    label: text,
    name: text,
    city: parts[0] || text,
    country: parts[parts.length - 1] || "",
  };
};

const questionPayload = (question, index) => {
  const options = Array.isArray(question.answerOptions)
    ? normalizeTagList(question.answerOptions)
    : normalizeTagList(String(question.options || "").split(/[\n,]+/));
  const payload = {
    question: question.question.trim(),
    questionText: question.question.trim(),
    answerType: question.answerType || "TEXT",
    answerValueType: question.answerValueType || defaultAnswerValueType(question.answerType),
    isRequired: question.isRequired !== false,
    displayOrder: index,
    orderNumber: index + 1,
  };
  if (options.length) payload.answerOptions = options;
  if (question.answerMetric?.trim()) payload.answerMetric = question.answerMetric.trim();
  return payload;
};

const isServerQuestionId = (id) => {
  const value = String(id || "");
  return value && !value.startsWith("sample-") && !value.startsWith("question-");
};

const pageContent = (data) => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.content)) return data.content;
  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(data?.items)) return data.items;
  return [];
};

const defaultAnswerValueType = (answerType) => {
  const normalized = String(answerType || "").toUpperCase();
  if (normalized === "NUMBER") return "NUMBER";
  if (normalized === "DATE") return "DATE";
  if (normalized === "DATETIME") return "DATETIME";
  if (normalized === "TIME") return "TIME";
  if (normalized === "BOOLEAN") return "BOOLEAN";
  return "TEXT";
};

const isChoiceAnswerType = (answerType) =>
  CHOICE_ANSWER_TYPES.has(String(answerType || "").toUpperCase());

const parseOptionText = (value) =>
  String(value || "")
    .split(/\n+/)
    .map((option) => option.trim())
    .filter(Boolean);

const questionOptions = (question) => {
  const options = question?.answerOptions;
  if (!Array.isArray(options)) return [];
  return options
    .map((option) => {
      if (typeof option === "string" || typeof option === "number" || typeof option === "boolean") return String(option);
      if (option && typeof option === "object") {
        return String(option.label || option.name || option.title || option.value || option.text || "").trim();
      }
      return "";
    })
    .filter(Boolean);
};

const catalogOptions = (items = []) =>
  items
    .map((item) => {
      const id = item.id || item.code || item.name;
      const label = item.name || item.code || item.description || "";
      return id && label ? { value: id, label } : null;
    })
    .filter(Boolean);

const selectedCatalogIds = (values, items) => {
  const ids = new Set(items.map((item) => item.id).filter(Boolean));
  return normalizeTagList(values).filter((value) => ids.has(value));
};

const selectedCustomLabels = (values, items, options) => {
  const ids = new Set(items.map((item) => item.id).filter(Boolean));
  const optionMap = new Map(options.map((option) => [option.value, option.label]));
  return normalizeTagList(values)
    .filter((value) => !ids.has(value))
    .map((value) => optionMap.get(value) || value)
    .filter(Boolean);
};

const organizationUserProfile = (member = {}) => {
  const nested = member.userProfile || member.user || {};
  const firstName = member.firstName || nested.firstName || "";
  const lastName = member.lastName || nested.lastName || "";
  const email = member.email || member.primaryEmail || nested.email || nested.primaryEmail || "";
  const name =
    member.fullName ||
    member.name ||
    nested.fullName ||
    nested.name ||
    [firstName, lastName].filter(Boolean).join(" ");
  return {
    id: member.userId || nested.id || member.id || "",
    name: name || email || member.userId || member.id || "Organization user",
    email: email || "Email not available",
  };
};

const roleOptionsFor = (roles, query) => {
  const normalizedQuery = String(query || "").trim().toLowerCase();
  if (!normalizedQuery) return [];

  return roles
    .map((role, index) => {
      const title = String(role.title || "").trim();
      const normalizedTitle = title.toLowerCase();
      const synonyms = Array.isArray(role.synonyms)
        ? role.synonyms.map((value) => String(value || "").toLowerCase())
        : [];
      const description = String(role.description || "").toLowerCase();
      let rank = Number.POSITIVE_INFINITY;

      if (normalizedTitle === normalizedQuery) rank = 0;
      else if (normalizedTitle.startsWith(normalizedQuery)) rank = 1;
      else if (normalizedTitle.includes(normalizedQuery)) rank = 2;
      else if (synonyms.some((value) => value === normalizedQuery)) rank = 3;
      else if (synonyms.some((value) => value.startsWith(normalizedQuery))) rank = 4;
      else if (synonyms.some((value) => value.includes(normalizedQuery))) rank = 5;
      else if (description.includes(normalizedQuery)) rank = 6;

      return { role, rank, index };
    })
    .filter((item) => Number.isFinite(item.rank) && item.role.title)
    .sort((left, right) =>
      left.rank - right.rank ||
      String(left.role.title).localeCompare(String(right.role.title)) ||
      left.index - right.index,
    )
    .map(({ role }) => ({
      value: role.title,
      label: role.title,
    }))
    .slice(0, 8);
};

export default function NewJobPost() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { appConfig } = useAppConfig();
  const {
    selectedWorkspace,
    selectedWorkspaceId,
    selectedWorkspaceUse,
    workspaceAccountMode,
    isEmployerWorkspace,
    workspaceUseLoading,
    workspaceUseReady,
    loading: workspaceLoading,
  } = useWorkspace();
  const actorId = user?.id || user?.userId || "";
  const editJobIdFromRoute = useMemo(() => {
    const stateId = location?.state?.jobOpportunityId || location?.state?.jobPost?.id;
    const query = new URLSearchParams(location.search || "");
    return stateId || query.get("jobOpportunityId") || query.get("jobId") || "";
  }, [location.search, location?.state]);
  const [form] = Form.useForm();
  const [questions, setQuestions] = useState([]);
  const [loadedQuestionIds, setLoadedQuestionIds] = useState([]);
  const [questionSearch, setQuestionSearch] = useState("");
  const [composeOpen, setComposeOpen] = useState(false);
  const [composeQuestion, setComposeQuestion] = useState("");
  const [composeAnswerType, setComposeAnswerType] = useState("TEXT");
  const [composeAnswerValueType, setComposeAnswerValueType] = useState("TEXT");
  const [composeAnswerOptions, setComposeAnswerOptions] = useState([]);
  const [composeOptionDraft, setComposeOptionDraft] = useState("");
  const [composeAnswerMetric, setComposeAnswerMetric] = useState("");
  const [composeRequired, setComposeRequired] = useState(true);
  const [sampleQuestions, setSampleQuestions] = useState([]);
  const [sampleQuestionsLoading, setSampleQuestionsLoading] = useState(false);
  const [sampleQuestionsError, setSampleQuestionsError] = useState("");
  const [professionalSpecializations, setProfessionalSpecializations] = useState([]);
  const [professionalSkills, setProfessionalSkills] = useState([]);
  const [catalogsLoading, setCatalogsLoading] = useState(false);
  const [catalogsError, setCatalogsError] = useState("");
  const [serviceOfferings, setServiceOfferings] = useState([]);
  const [serviceOfferingsLoading, setServiceOfferingsLoading] = useState(false);
  const [serviceOfferingsError, setServiceOfferingsError] = useState("");
  const [organizationUsers, setOrganizationUsers] = useState([]);
  const [organizationUsersLoading, setOrganizationUsersLoading] = useState(false);
  const [hiringTeamMembers, setHiringTeamMembers] = useState([]);
  const [professionRoles, setProfessionRoles] = useState([]);
  const [professionRolesLoading, setProfessionRolesLoading] = useState(false);
  const [professionRolesError, setProfessionRolesError] = useState("");
  const [titleSearch, setTitleSearch] = useState("");
  const [savingAction, setSavingAction] = useState("");
  const [editingJobId, setEditingJobId] = useState(null);
  const [editLoading, setEditLoading] = useState(false);
  const [aiModalOpen, setAiModalOpen] = useState(false);
  const [aiPrompt, setAiPrompt] = useState("");
  const [aiGenerating, setAiGenerating] = useState(false);
  const [aiDraft, setAiDraft] = useState(null);
  const [aiError, setAiError] = useState("");
  const aiUsage = useAiRequestUsage(selectedWorkspace?.id);
  const googleMapsApiKey =
    appConfig?.googleMapsApiKey ||
    appConfig?.googleMapsApiMatrixKey ||
    process.env.REACT_APP_GOOGLE_MAPS_API_KEY ||
    "";

  const specializationOptions = useMemo(() => catalogOptions(professionalSpecializations), [professionalSpecializations]);
  const skillOptions = useMemo(() => catalogOptions(professionalSkills), [professionalSkills]);
  const workAreaOptions = useMemo(
    () =>
      serviceOfferings
        .map((service) => {
          const id = service.id || service.code || service.name;
          const label = service.briefName || service.name || titleCase(service.code) || "";
          return id && label ? { value: id, label } : null;
        })
        .filter(Boolean),
    [serviceOfferings],
  );
  const workAreaIds = useMemo(() => new Set(workAreaOptions.map((option) => option.value)), [workAreaOptions]);
  const organizationUserOptions = useMemo(
    () =>
      organizationUsers
        .map((member) => {
          const profile = organizationUserProfile(member);
          if (!profile.id) return null;
          return {
            value: profile.id,
            label: `${profile.name}${profile.email ? ` · ${profile.email}` : ""}`,
            profile,
          };
        })
        .filter(Boolean),
    [organizationUsers],
  );
  const selectedHiringTeamIds = useMemo(
    () => new Set(hiringTeamMembers.map((member) => member.userId).filter(Boolean)),
    [hiringTeamMembers],
  );
  const availableHiringTeamOptions = useMemo(
    () => organizationUserOptions.filter((option) => !selectedHiringTeamIds.has(option.value)),
    [organizationUserOptions, selectedHiringTeamIds],
  );
  const titleOptions = useMemo(
    () => roleOptionsFor(professionRoles, titleSearch),
    [professionRoles, titleSearch],
  );
  const selectedQuestionTexts = useMemo(
    () => new Set(questions.map((question) => question.question.trim().toLowerCase()).filter(Boolean)),
    [questions],
  );
  const filteredSampleQuestions = useMemo(() => {
    const query = questionSearch.trim().toLowerCase();
    if (!query) return [];
    return sampleQuestions
      .filter((question) => {
        const text = String(question.question || "").trim();
        return text && !selectedQuestionTexts.has(text.toLowerCase()) && text.toLowerCase().includes(query);
      })
      .slice(0, 8);
  }, [questionSearch, sampleQuestions, selectedQuestionTexts]);

  const initialValues = useMemo(() => ({
    postStartDate: today(),
    postEndDate: addMonths(1),
    showPosterOnPost: true,
    posterContactable: true,
    workSchedules: ["ANY"],
    requiredEducationLevels: ["ANY"],
    locationType: "ONSITE",
  }), []);

  const addSampleQuestion = (sampleId) => {
    const sample = sampleQuestions.find((item) => String(item.id || item.question) === String(sampleId));
    if (!sample?.question) return;
    const text = sample.question.trim();
    setQuestions((current) => {
      const exists = current.some((question) => question.question.trim().toLowerCase() === text.toLowerCase());
      if (exists) return current;
      return [
        ...current,
        {
          id: `sample-${sample.id || Date.now()}`,
          question: text,
          answerType: sample.answerType || "TEXT",
          answerValueType: sample.answerValueType || defaultAnswerValueType(sample.answerType),
          isRequired: sample.isRequired !== false,
          answerOptions: questionOptions(sample),
          options: questionOptions(sample).join("\n"),
          answerMetric: sample.answerMetric || sample.metric || "",
        },
      ];
    });
    setQuestionSearch("");
  };

  const updateQuestion = (id, patch) => {
    setQuestions((current) => current.map((question) => (question.id === id ? { ...question, ...patch } : question)));
  };

  const removeQuestion = (id) => {
    setQuestions((current) => current.filter((question) => question.id !== id));
  };

  const toggleQuestionRequired = (id) => {
    setQuestions((current) =>
      current.map((question) =>
        question.id === id ? { ...question, isRequired: question.isRequired === false } : question,
      ),
    );
  };

  const resetCompose = () => {
    setComposeQuestion("");
    setComposeAnswerType("TEXT");
    setComposeAnswerValueType("TEXT");
    setComposeAnswerOptions([]);
    setComposeOptionDraft("");
    setComposeAnswerMetric("");
    setComposeRequired(true);
    setComposeOpen(false);
  };

  const selectComposeAnswerType = (answerType) => {
    setComposeAnswerType(answerType);
    setComposeAnswerValueType(defaultAnswerValueType(answerType));
    if (!isChoiceAnswerType(answerType)) {
      setComposeAnswerOptions([]);
      setComposeOptionDraft("");
    }
  };

  const addComposeOptions = () => {
    const nextOptions = parseOptionText(composeOptionDraft);
    if (!nextOptions.length) return;
    setComposeAnswerOptions((current) => {
      const existing = new Set(current.map((option) => option.toLowerCase()));
      return [
        ...current,
        ...nextOptions.filter((option) => !existing.has(option.toLowerCase())),
      ];
    });
    setComposeOptionDraft("");
  };

  const addComposedQuestion = () => {
    const text = composeQuestion.trim();
    if (!text) return;
    if (selectedQuestionTexts.has(text.toLowerCase())) {
      message.warning("That question is already added.");
      return;
    }
    if (isChoiceAnswerType(composeAnswerType) && !composeAnswerOptions.length) {
      message.warning("Add at least one option for this answer type.");
      return;
    }
    setQuestions((current) => [
      ...current,
      {
        id: `question-${Date.now()}`,
        question: text,
        answerType: composeAnswerType,
        answerValueType: composeAnswerValueType,
        isRequired: composeRequired,
        answerOptions: isChoiceAnswerType(composeAnswerType) ? composeAnswerOptions : null,
        options: composeAnswerOptions.join("\n"),
        answerMetric: composeAnswerMetric.trim(),
      },
    ]);
    resetCompose();
  };

  const addHiringTeamMember = (userId) => {
    const option = organizationUserOptions.find((item) => item.value === userId);
    if (!option) return;
    setHiringTeamMembers((current) => {
      if (current.some((member) => member.userId === userId)) return current;
      return [
        ...current,
        {
          userId,
          name: option.profile.name,
          email: option.profile.email,
          role: "INTERVIEWER",
          privileges: ["INTERVIEW"],
        },
      ];
    });
  };

  const updateHiringTeamMember = (userId, patch) => {
    setHiringTeamMembers((current) =>
      current.map((member) => (member.userId === userId ? { ...member, ...patch } : member)),
    );
  };

  const removeHiringTeamMember = (userId) => {
    setHiringTeamMembers((current) => current.filter((member) => member.userId !== userId));
  };

  const syncJobQuestions = async (jobId) => {
    if (!jobId) return;
    const validQuestions = questions
      .map((question) => ({ ...question, question: question.question.trim() }))
      .filter((question) => question.question);
    const currentServerIds = new Set(
      validQuestions.map((question) => (isServerQuestionId(question.id) ? question.id : "")).filter(Boolean),
    );
    const removedIds = loadedQuestionIds.filter((questionId) => !currentServerIds.has(questionId));

    await Promise.all(
      removedIds.map((questionId) =>
        olreadiaService.jobOpportunityQuestions.delete(jobId, questionId, actorId),
      ),
    );

    await Promise.all(
      validQuestions.map((question, index) => {
        const payload = questionPayload(question, index);
        if (isServerQuestionId(question.id)) {
          return olreadiaService.jobOpportunityQuestions.update(jobId, question.id, payload, actorId);
        }
        return olreadiaService.jobOpportunityQuestions.create(jobId, payload, actorId);
      }),
    );
  };

  useEffect(() => {
    let cancelled = false;
    const jobOpportunityId = editJobIdFromRoute;
    setEditingJobId(jobOpportunityId || null);
    if (!jobOpportunityId) {
      setLoadedQuestionIds([]);
    }

    const loadForEdit = async () => {
      if (!jobOpportunityId || !actorId) return;
      setEditLoading(true);
      try {
        const [job, questionsPage, hireMembers] = await Promise.all([
          olreadiaService.jobOpportunities.get(jobOpportunityId, actorId),
          olreadiaService.jobOpportunityQuestions.list(jobOpportunityId, actorId).catch(() => []),
          olreadiaService.jobOpportunityHireMembers.list(jobOpportunityId, actorId).catch(() => []),
        ]);

        if (cancelled) return;

        // Map job fields into form fields
        const today = () => new Date().toISOString().slice(0, 10);
        const addMonths = (months) => {
          const date = new Date();
          date.setMonth(date.getMonth() + months);
          return date.toISOString().slice(0, 10);
        };

        const values = {
          title: job?.title || "",
          description: job?.description || "",
          postStartDate: job?.postStartDate || today(),
          postEndDate: job?.postEndDate || addMonths(1),
          workSchedules: job?.workSchedules && job.workSchedules.length ? job.workSchedules : ["ANY"],
          locationType: job?.locationType || "ONSITE",
          jobLocations: Array.isArray(job?.requiredLocations) ? job.requiredLocations.map((l) => (l.label || l.name || l)) : [],
          showPosterOnPost: job?.showPosterOnPost !== undefined ? job.showPosterOnPost : true,
          posterContactable: job?.posterContactable !== undefined ? job.posterContactable : true,
          requiredYearsOfExperience: job?.requiredYearsOfExperience || null,
          requiredSkills: Array.isArray(job?.otherRequiredSkills) ? job.otherRequiredSkills : [],
          requiredSpecializations: Array.isArray(job?.otherRequiredSpecializations) ? job.otherRequiredSpecializations : [],
          requiredWorkAreas: Array.isArray(job?.otherRequiredWorkAreas) ? job.otherRequiredWorkAreas : [],
          requiredEducationLevels: job?.requiredEducationLevels || ["ANY"],
        };

        form.setFieldsValue(values);
        const loadedQuestions = pageContent(questionsPage) || [];
        setLoadedQuestionIds(loadedQuestions.map((question) => question.id).filter(Boolean));
        setQuestions(loadedQuestions.map((q) => ({
          id: q.id,
          question: q.question || q.questionText || "",
          answerType: q.answerType || "TEXT",
          answerValueType: q.answerValueType || "TEXT",
          isRequired: q.isRequired !== undefined ? q.isRequired : true,
          answerOptions: Array.isArray(q.answerOptions) ? q.answerOptions : [],
          options: Array.isArray(q.answerOptions) ? q.answerOptions.join("\n") : "",
          answerMetric: q.answerMetric || q.metric || "",
        })));
        setHiringTeamMembers((hireMembers || []).map((m) => ({
          ...m,
          role: m.role || "INTERVIEWER",
          privileges: Array.isArray(m.privileges) ? m.privileges : ["INTERVIEW"],
        })));
      } catch (err) {
        if (!cancelled) message.error(err?.message || "Unable to load job post for editing.");
      } finally {
        if (!cancelled) setEditLoading(false);
      }
    };

    loadForEdit();
    const loadSuggestions = async () => {
      if (!actorId) return;
      setCatalogsLoading(true);
      setServiceOfferingsLoading(true);
      setSampleQuestionsLoading(true);
      setProfessionRolesLoading(true);
      setCatalogsError("");
      setServiceOfferingsError("");
      setSampleQuestionsError("");
      setProfessionRolesError("");
      const [specializationResult, skillResult, serviceResult, sampleResult, roleResult] = await Promise.allSettled([
        olreadiaService.professionalCatalogs.specializations(actorId),
        olreadiaService.professionalCatalogs.skills(actorId),
        olreadiaService.professionProfiles.listServiceOfferings(actorId, { page: 0, size: 1000 }),
        olreadiaService.jobOpportunityQuestions.samples(actorId, { page: 0, size: 1000 }),
        olreadiaService.professionRoles.list(actorId),
      ]);
      if (cancelled) return;
      if (specializationResult.status === "fulfilled") setProfessionalSpecializations(specializationResult.value);
      else {
        setProfessionalSpecializations([]);
        setCatalogsError(specializationResult.reason?.message || "Failed to load professional catalogs.");
      }
      if (skillResult.status === "fulfilled") setProfessionalSkills(skillResult.value);
      else {
        setProfessionalSkills([]);
        setCatalogsError(skillResult.reason?.message || "Failed to load professional catalogs.");
      }
      if (serviceResult.status === "fulfilled") setServiceOfferings(pageContent(serviceResult.value).filter((service) => service.active !== false));
      else {
        setServiceOfferings([]);
        setServiceOfferingsError(serviceResult.reason?.message || "Failed to load work areas.");
      }
      if (sampleResult.status === "fulfilled") setSampleQuestions(sampleResult.value);
      else {
        setSampleQuestions([]);
        setSampleQuestionsError(sampleResult.reason?.message || "Failed to load sample questions.");
      }
      if (roleResult.status === "fulfilled") setProfessionRoles(roleResult.value);
      else {
        setProfessionRoles([]);
        setProfessionRolesError(roleResult.reason?.message || "Failed to load title suggestions.");
      }
      setCatalogsLoading(false);
      setServiceOfferingsLoading(false);
      setSampleQuestionsLoading(false);
      setProfessionRolesLoading(false);
    };
    loadSuggestions().catch((error) => {
      if (!cancelled) {
        setCatalogsError(error?.message || "Unable to load suggestions.");
        setServiceOfferingsError(error?.message || "Unable to load suggestions.");
        setSampleQuestionsError(error?.message || "Unable to load suggestions.");
        setProfessionRolesError(error?.message || "Unable to load title suggestions.");
        setCatalogsLoading(false);
        setServiceOfferingsLoading(false);
        setSampleQuestionsLoading(false);
        setProfessionRolesLoading(false);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [actorId, editJobIdFromRoute, form]);

  useEffect(() => {
    let cancelled = false;
    const loadOrganizationUsers = async () => {
      const organizationId = selectedWorkspace?.organizationId;
      if (!organizationId || !actorId) {
        setOrganizationUsers([]);
        return;
      }
      setOrganizationUsersLoading(true);
      try {
        const organization = await olreadiaService.organizations.get(organizationId);
        if (!cancelled) setOrganizationUsers(organization?.users || organization?.organizationUsers || []);
      } catch {
        if (!cancelled) setOrganizationUsers([]);
      } finally {
        if (!cancelled) setOrganizationUsersLoading(false);
      }
    };
    loadOrganizationUsers();
    return () => {
      cancelled = true;
    };
  }, [actorId, selectedWorkspace?.organizationId]);

  const openAiGenerator = () => {
    const values = form.getFieldsValue();
    setAiPrompt(values.title ? `Create a job description for ${values.title}.` : "");
    setAiDraft(null);
    setAiError("");
    setAiModalOpen(true);
  };

  const generateAiDescription = async () => {
    if (!aiPrompt.trim()) {
      message.warning("Describe the role you want to generate.");
      return;
    }
    setAiGenerating(true);
    setAiDraft(null);
    setAiError("");
    try {
      const values = form.getFieldsValue();
      const educationLevels = normalizeTagList(values.requiredEducationLevels);
      const skills = normalizeTagList(values.requiredSkills);
      const context = {
        ...(values.title?.trim() ? { title: values.title.trim() } : {}),
        ...(educationLevels.length && !educationLevels.includes("ANY")
          ? { requiredEducationLevel: educationLevels[0] } : {}),
        ...(Number.isFinite(Number(values.requiredYearsOfExperience))
          ? { requiredYearsOfExperience: Number(values.requiredYearsOfExperience) } : {}),
        ...(skills.length ? { requiredSkillNames: skills } : {}),
        ...(values.locationType ? { locationType: values.locationType } : {}),
      };

      const result = await olreadiaService.jobOpportunities.generateDescription(
        { prompt: aiPrompt.trim(), ...(Object.keys(context).length ? { context } : {}) },
        actorId,
      );
      setAiDraft(result);
    } catch (error) {
      const status = error?.response?.status || error?.status;
      if (status === 429) {
        setAiError("AI request limit reached. Upgrade your plan to continue generating.");
        if (aiUsage?.refetch) aiUsage.refetch();
      } else {
        setAiError(error?.message || "Failed to generate job description.");
      }
    } finally {
      setAiGenerating(false);
    }
  };

  const applyAiDraft = () => {
    if (!aiDraft) return;
    const patch = {};
    if (aiDraft.title) patch.title = aiDraft.title;

    // Build a rich HTML description from the generated structure
    const htmlParts = [];
    if (aiDraft.summary) htmlParts.push(`<p><strong>Summary</strong></p><p>${escapeHtml(aiDraft.summary)}</p>`);
    if (aiDraft.description) htmlParts.push(`<p><strong>About the Role</strong></p><p>${escapeHtml(aiDraft.description)}</p>`);
    if (aiDraft.responsibilities?.length) {
      htmlParts.push(`<p><strong>Responsibilities</strong></p><ul>${aiDraft.responsibilities.map((r) => `<li>${escapeHtml(r)}</li>`).join("")}</ul>`);
    }
    if (aiDraft.requirements?.length) {
      htmlParts.push(`<p><strong>Requirements</strong></p><ul>${aiDraft.requirements.map((r) => `<li>${escapeHtml(r)}</li>`).join("")}</ul>`);
    }
    if (aiDraft.preferredQualifications?.length) {
      htmlParts.push(`<p><strong>Preferred Qualifications</strong></p><ul>${aiDraft.preferredQualifications.map((r) => `<li>${escapeHtml(r)}</li>`).join("")}</ul>`);
    }
    if (aiDraft.benefits?.length) {
      htmlParts.push(`<p><strong>Benefits</strong></p><ul>${aiDraft.benefits.map((r) => `<li>${escapeHtml(r)}</li>`).join("")}</ul>`);
    }
    if (aiDraft.softSkills?.length) {
      htmlParts.push(`<p><strong>Soft Skills</strong></p><p>${escapeHtml(aiDraft.softSkills.join(", "))}</p>`);
    }
    if (htmlParts.length) patch.description = htmlParts.join("\n");

    // Fill skills from the generated draft
    if (aiDraft.requiredSkills?.length) {
      const currentSkills = normalizeTagList(form.getFieldValue("requiredSkills"));
      const merged = [...new Set([...currentSkills, ...aiDraft.requiredSkills])];
      patch.requiredSkills = merged;
    }

    form.setFieldsValue(patch);
    setAiModalOpen(false);
    setAiDraft(null);
    setAiPrompt("");
    message.success("AI-generated content applied. Review and adjust before publishing.");
  };

  const closeAiGenerator = () => {
    setAiModalOpen(false);
    setAiDraft(null);
    setAiPrompt("");
    setAiError("");
  };

  const submit = async (status) => {
    if (!actorId) {
      message.warning("Sign in before posting a job.");
      return;
    }
    if (!selectedWorkspaceId || !isEmployerWorkspace) {
      message.warning("Switch to a hiring workspace before posting a job.");
      return;
    }

    try {
      const values = await form.validateFields();
      const locationType = values.locationType || "ONSITE";
      const locations = locationType === "REMOTE"
        ? []
        : normalizeTagList(values.jobLocations).map(locationPayload).filter(Boolean);
      if (status === "OPEN" && locationType !== "REMOTE" && !locations.length) {
        message.warning("Add at least one job location, or select Remote.");
        return;
      }

      const invalidQuestion = questions.find((question) => {
        if (!question.question.trim()) return false;
        if (!["SINGLE_CHOICE", "MULTIPLE_CHOICE"].includes(question.answerType)) return false;
        return normalizeTagList(String(question.options || "").split(",")).length === 0;
      });
      if (invalidQuestion) {
        message.warning(`Add answer options for "${invalidQuestion.question.trim()}".`);
        return;
      }

      const workSchedules = normalizeTagList(values.workSchedules);
      const concreteSchedules = workSchedules.filter((value) => value !== "ANY");
      const educationLevels = normalizeTagList(values.requiredEducationLevels);
      const skills = normalizeTagList(values.requiredSkills);
      const specializations = normalizeTagList(values.requiredSpecializations);
      const workAreas = normalizeTagList(values.requiredWorkAreas);
      const requiredSkillIds = selectedCatalogIds(skills, professionalSkills);
      const requiredSpecializationIds = selectedCatalogIds(specializations, professionalSpecializations);
      const requiredWorkAreaIds = workAreas.filter((value) => workAreaIds.has(value));

      const payload = {
        title: values.title.trim(),
        description: values.description?.trim() || null,
        postStartDate: values.postStartDate || null,
        postEndDate: values.postEndDate || null,
        workSchedule: concreteSchedules.length === 1 ? concreteSchedules[0] : null,
        workSchedules: workSchedules.includes("ANY") ? ["ANY"] : concreteSchedules,
        posterType: workspaceAccountMode === "ORGANIZATION" ? "BUSINESS" : "USER",
        showPosterOnPost: values.showPosterOnPost !== false,
        posterContactable: values.posterContactable !== false,
        workspaceId: selectedWorkspaceId,
        status,
        requiredSpecializationIds,
        otherRequiredSpecializations: selectedCustomLabels(specializations, professionalSpecializations, specializationOptions),
        requiredSkillIds,
        otherRequiredSkills: selectedCustomLabels(skills, professionalSkills, skillOptions),
        requiredWorkAreaIds,
        otherRequiredWorkAreas: workAreas.filter((value) => !workAreaIds.has(value)),
        requiredEducationLevel: educationLevels.includes("ANY") ? null : educationLevels[0] || null,
        requiredEducationLevels: educationLevels.includes("ANY") ? ["ANY"] : educationLevels,
        requiredYearsOfExperience: Number.isFinite(Number(values.requiredYearsOfExperience))
          ? Number(values.requiredYearsOfExperience)
          : null,
        jobLocations: locations,
        requiredLocations: locations,
        locationType,
      };

      setSavingAction(status);
      let jobId = null;
      if (editingJobId) {
        const updated = await olreadiaService.jobOpportunities.patch(editingJobId, payload, actorId);
        jobId = updated?.id || editingJobId;
      } else {
        const created = await olreadiaService.jobOpportunities.create(payload, actorId);
        jobId = created?.id;
      }
      if (jobId) await syncJobQuestions(jobId);
      if (jobId && hiringTeamMembers.length) {
        // create hire members without ids
        const toCreateMembers = hiringTeamMembers.filter((m) => !m.id);
        if (toCreateMembers.length) {
          await Promise.all(toCreateMembers.map((member) =>
            olreadiaService.jobOpportunityHireMembers.create(jobId, {
              userId: member.userId,
              role: member.role || "INTERVIEWER",
              privileges: member.privileges?.length ? member.privileges : ["INTERVIEW"],
            }, actorId),
          ));
        }
      }
      message.success(status === "OPEN" ? "Job post published." : "Draft saved.");
      navigate(paths.jobPosts);
    } catch (error) {
      if (error?.errorFields) return;
      message.error(error?.message || "Unable to save job post.");
    } finally {
      setSavingAction("");
    }
  };

  if (editLoading) {
    return (
      <div className="olreadia-flow-page">
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(paths.jobPosts)} style={{ marginBottom: 12 }}>
          Back to job posts
        </Button>
        <Card className="olreadia-flow-card">
          <Skeleton active paragraph={{ rows: 8 }} />
        </Card>
      </div>
    );
  }

  return (
    <div className="olreadia-flow-page">
      <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(paths.jobPosts)} style={{ marginBottom: 12 }}>
        Back to job posts
      </Button>

      <div className="olreadia-flow-layout">
        <main className="olreadia-flow-main">
          <section className="olreadia-flow-hero">
            <span className="olreadia-flow-hero__icon"><SolutionOutlined /></span>
            <div>
              <Text className="olreadia-flow-eyebrow">Hiring workspace</Text>
              <Title level={2}>Post a Job</Title>
              <Paragraph>
                {selectedWorkspace?.name || "Selected workspace"} · {titleCase(selectedWorkspaceUse || "HIRING")}
              </Paragraph>
            </div>
            <Space wrap>
              {aiUsage?.hasReachedLimit ? (
                <Button
                  type="primary"
                  icon={<ThunderboltOutlined />}
                  onClick={() => navigate(paths.workspaceSubscriptions(selectedWorkspace?.id))}
                >
                  Upgrade to Generate
                </Button>
              ) : (
                <Button icon={<ThunderboltOutlined />} onClick={openAiGenerator}>
                  Generate with AI
                </Button>
              )}
              <Button icon={<SaveOutlined />} loading={savingAction === "DRAFT"} onClick={() => submit("DRAFT")}>
                Save Draft
              </Button>
              <Button type="primary" icon={<SendOutlined />} loading={savingAction === "OPEN"} onClick={() => submit("OPEN")}>
                Publish
              </Button>
            </Space>
          </section>

          {selectedWorkspaceId && !workspaceLoading && workspaceUseReady && !isEmployerWorkspace ? (
            <Alert
              type="warning"
              showIcon
              message="Switch to a hiring workspace"
              description="Job posts are created from an organization or hiring workspace context."
              style={{ marginBottom: 12 }}
            />
          ) : null}

          <Form form={form} layout="vertical" initialValues={initialValues} className="olreadia-job-post-form">
            <Card className="olreadia-flow-card">
              <Title level={4}>Job Details</Title>
              <div className="olreadia-flow-form-grid">
                <Form.Item name="title" label="Title" rules={[{ required: true, message: "Enter a job title." }]}>
                  <AutoComplete
                    options={titleOptions}
                    onSearch={setTitleSearch}
                    onSelect={(value) => {
                      setTitleSearch(value);
                      form.setFieldsValue({ title: value });
                    }}
                    onChange={(value) => {
                      setTitleSearch(value);
                      form.setFieldsValue({ title: value });
                    }}
                    filterOption={false}
                    notFoundContent={professionRolesLoading ? "Loading suggestions..." : null}
                  >
                    <Input placeholder="Senior caregiver, React developer..." />
                  </AutoComplete>
                </Form.Item>
                <Form.Item name="requiredYearsOfExperience" label="Years of Experience">
                  <InputNumber min={0} max={60} placeholder="0" style={{ width: "100%" }} />
                </Form.Item>
              </div>
              {professionRolesError ? (
                <Alert
                  type="warning"
                  showIcon
                  message="Title suggestions could not be loaded"
                  description={professionRolesError}
                  style={{ marginBottom: 12 }}
                />
              ) : null}
              <Form.Item name="description" label="Description">
                <MentionEditor
                  value={form.getFieldValue("description") || ""}
                  onChange={(html) => form.setFieldsValue({ description: html })}
                  users={organizationUsers}
                  height="180px"
                  placeholder="Describe the role, expectations, and ideal candidate."
                  debugId="NewJobPost:Description"
                />
              </Form.Item>
              <div className="olreadia-flow-form-grid">
                <Form.Item name="showPosterOnPost" label="Show poster on post" valuePropName="checked">
                  <Switch />
                </Form.Item>
                <Form.Item name="posterContactable" label="Poster contactable" valuePropName="checked">
                  <Switch />
                </Form.Item>
              </div>
            </Card>

            <Card className="olreadia-flow-card">
              <Title level={4}>Dates & Work Style</Title>
              <div className="olreadia-flow-form-grid">
                <Form.Item name="postStartDate" label="Post Start Date">
                  <Input type="date" />
                </Form.Item>
                <Form.Item name="postEndDate" label="Post End Date">
                  <Input type="date" />
                </Form.Item>
                <Form.Item name="workSchedules" label="Job Type">
                  <Select mode="multiple" options={WORK_SCHEDULES} />
                </Form.Item>
                <Form.Item name="locationType" label="Location Type">
                  <Select
                    options={[
                      { label: "Onsite", value: "ONSITE" },
                      { label: "Remote", value: "REMOTE" },
                      { label: "Hybrid", value: "HYBRID" },
                    ]}
                  />
                </Form.Item>
              </div>
              <Form.Item name="jobLocations" label="Job Locations">
                <GooglePlacesTagsSelect
                  googleMapsApiKey={googleMapsApiKey}
                  placeholder="Search city or country e.g. Seattle, US"
                  types={["(cities)"]}
                  resolveValueFromDetails={regionSummaryFromPlaceDetails}
                />
              </Form.Item>
            </Card>

            <Card className="olreadia-flow-card">
              <Title level={4}>Candidate Requirements</Title>
              <div className="olreadia-flow-form-grid">
                <Form.Item name="requiredEducationLevels" label="Education Level">
                  <Select mode="multiple" options={EDUCATION_LEVELS} />
                </Form.Item>
                <Form.Item name="requiredSkills" label="Skills">
                  <Select
                    mode="tags"
                    tokenSeparators={[","]}
                    showSearch
                    loading={catalogsLoading}
                    options={skillOptions}
                    optionFilterProp="label"
                    placeholder="Skill e.g. React, CPR"
                  />
                </Form.Item>
                <Form.Item name="requiredSpecializations" label="Specializations / Fields of Study">
                  <Select
                    mode="tags"
                    tokenSeparators={[","]}
                    showSearch
                    loading={catalogsLoading}
                    options={specializationOptions}
                    optionFilterProp="label"
                    placeholder="Field e.g. Nursing, Computer Science"
                  />
                </Form.Item>
                <Form.Item name="requiredWorkAreas" label="Work Areas">
                  <Select
                    mode="tags"
                    tokenSeparators={[","]}
                    showSearch
                    loading={serviceOfferingsLoading}
                    options={workAreaOptions}
                    optionFilterProp="label"
                    placeholder="Service e.g. home care, tutoring"
                  />
                </Form.Item>
              </div>
              {catalogsError || serviceOfferingsError ? (
                <Alert
                  type="warning"
                  showIcon
                  message="Some suggestions could not be loaded"
                  description={[catalogsError, serviceOfferingsError].filter(Boolean).join(" ")}
                />
              ) : null}
            </Card>

            <Card className="olreadia-flow-card">
              <div className="olreadia-flow-section-heading">
                <div>
                  <Title level={4}>Application Questions</Title>
                  <Paragraph>Ask screening questions candidates answer while applying.</Paragraph>
                </div>
                <Button type="primary" icon={<PlusOutlined />} onClick={() => setComposeOpen(true)}>
                  Compose Question
                </Button>
              </div>

              {questions.length ? (
                <div className="olreadia-job-questions-list">
                  {questions.map((question, index) => (
                    <Card key={question.id} className="olreadia-job-question-card">
                      <div className="olreadia-job-question-card__top">
                        <span className="olreadia-job-question-card__order">{index + 1}</span>
                        <Input.TextArea
                          autoSize={{ minRows: 1, maxRows: 3 }}
                          value={question.question}
                          onChange={(event) => updateQuestion(question.id, { question: event.target.value })}
                          placeholder={`Question ${index + 1}`}
                        />
                        <Button danger icon={<DeleteOutlined />} onClick={() => removeQuestion(question.id)} />
                      </div>
                      <div className="olreadia-job-question-card__footer">
                        <Space wrap size={6}>
                          <Tag color="blue">{titleCase(question.answerType || "TEXT")}</Tag>
                          <Tag>{titleCase(question.answerValueType || defaultAnswerValueType(question.answerType))}</Tag>
                          {question.answerMetric ? <Tag color="gold">{question.answerMetric}</Tag> : null}
                        </Space>
                        <Button
                          size="small"
                          className={question.isRequired !== false ? "olreadia-required-toggle is-active" : "olreadia-required-toggle"}
                          onClick={() => toggleQuestionRequired(question.id)}
                        >
                          {question.isRequired === false ? "Optional" : "Required"}
                        </Button>
                      </div>
                      {questionOptions(question).length ? (
                        <Space wrap size={6} className="olreadia-question-option-chips">
                          {questionOptions(question).map((option) => <Tag key={option}>{option}</Tag>)}
                        </Space>
                      ) : null}
                    </Card>
                  ))}
                </div>
              ) : (
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No questions added yet." />
              )}

              <div className="olreadia-question-lookup">
                <div className="olreadia-question-lookup__bar">
                  <Input
                    allowClear
                    value={questionSearch}
                    onChange={(event) => setQuestionSearch(event.target.value)}
                    placeholder="Search questions e.g. availability, license"
                  />
                  <Button icon={<PlusOutlined />} onClick={() => setComposeOpen(true)}>
                    Compose
                  </Button>
                </div>
                <div className="olreadia-question-lookup__panel">
                  {sampleQuestionsLoading ? (
                    <Text type="secondary">Loading sample questions...</Text>
                  ) : sampleQuestionsError ? (
                    <Text type="danger">{sampleQuestionsError}</Text>
                  ) : !questionSearch.trim() ? (
                    <Text type="secondary">Search to add a screening question from the lookup.</Text>
                  ) : filteredSampleQuestions.length ? (
                    filteredSampleQuestions.map((question) => {
                      const key = String(question.id || question.question);
                      const options = questionOptions(question);
                      return (
                        <button
                          type="button"
                          key={key}
                          className="olreadia-question-suggestion"
                          onClick={() => {
                            addSampleQuestion(key);
                          }}
                        >
                          <span>
                            <strong>{question.question}</strong>
                            <small>{[titleCase(question.answerType || "TEXT"), titleCase(question.answerValueType || "")].filter(Boolean).join(" • ")}</small>
                            {options.length ? (
                              <span className="olreadia-question-suggestion__options">
                                {options.map((option) => <Tag key={option}>{option}</Tag>)}
                              </span>
                            ) : null}
                          </span>
                          <PlusOutlined />
                        </button>
                      );
                    })
                  ) : (
                    <Text type="secondary">No matching screening questions.</Text>
                  )}
                </div>
              </div>
            </Card>

            <Card className="olreadia-flow-card">
              <div className="olreadia-flow-card__top">
                <span className="olreadia-flow-card__icon"><TeamOutlined /></span>
                <div>
                  <Title level={4}>Hiring Team</Title>
                  <Paragraph>Add organization members who can interview, schedule, or help manage the post.</Paragraph>
                </div>
              </div>
              {!selectedWorkspace?.organizationId ? (
                <Alert
                  type="info"
                  showIcon
                  message="Switch to an organization workspace to add hiring team members."
                />
              ) : (
                <Space direction="vertical" size={12} style={{ width: "100%" }}>
                  <Select
                    showSearch
                    allowClear
                    loading={organizationUsersLoading}
                    options={availableHiringTeamOptions}
                    optionFilterProp="label"
                    placeholder="Search organization members"
                    onChange={(value) => value && addHiringTeamMember(value)}
                  />
                  {hiringTeamMembers.length ? (
                    <div className="olreadia-hiring-team-list">
                      {hiringTeamMembers.map((member) => (
                        <Card key={member.userId} className="olreadia-hiring-team-card">
                          <div>
                            <strong>{member.name}</strong>
                            <Text type="secondary">{member.email}</Text>
                          </div>
                          <Select
                            value={member.role}
                            options={HIRE_MEMBER_ROLES}
                            onChange={(role) => updateHiringTeamMember(member.userId, { role })}
                          />
                          <Select
                            mode="multiple"
                            value={member.privileges}
                            options={HIRE_MEMBER_PRIVILEGES}
                            onChange={(privileges) => updateHiringTeamMember(member.userId, { privileges })}
                          />
                          <Button danger icon={<DeleteOutlined />} onClick={() => removeHiringTeamMember(member.userId)}>
                            Remove
                          </Button>
                        </Card>
                      ))}
                    </div>
                  ) : (
                    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No hiring team members added yet." />
                  )}
                </Space>
              )}
            </Card>
          </Form>

          <Modal
            title="Compose Question"
            open={composeOpen}
            onCancel={resetCompose}
            onOk={addComposedQuestion}
            okText="Add Question"
            okButtonProps={{ disabled: !composeQuestion.trim() }}
            destroyOnClose={false}
            className="olreadia-compose-question-modal"
          >
            <Space direction="vertical" size={14} style={{ width: "100%" }}>
              <Input.TextArea
                autoSize={{ minRows: 3, maxRows: 6 }}
                value={composeQuestion}
                onChange={(event) => setComposeQuestion(event.target.value)}
                placeholder="Enter screening question"
              />
              <div className="olreadia-compose-question-grid">
                <div>
                  <Text className="olreadia-compose-question-label">Answer Type</Text>
                  <Select
                    value={composeAnswerType}
                    options={ANSWER_TYPES}
                    onChange={selectComposeAnswerType}
                    style={{ width: "100%" }}
                  />
                </div>
                <div>
                  <Text className="olreadia-compose-question-label">Answer Value Type</Text>
                  <Select
                    value={composeAnswerValueType}
                    options={ANSWER_VALUE_TYPES}
                    onChange={setComposeAnswerValueType}
                    style={{ width: "100%" }}
                  />
                </div>
              </div>

              <div>
                <Text className="olreadia-compose-question-label">Required</Text>
                <Space wrap>
                  <Button
                    type={composeRequired ? "primary" : "default"}
                    onClick={() => setComposeRequired(true)}
                    className={composeRequired ? "olreadia-compose-choice is-active" : "olreadia-compose-choice"}
                  >
                    Required
                  </Button>
                  <Button
                    type={!composeRequired ? "primary" : "default"}
                    onClick={() => setComposeRequired(false)}
                    className={!composeRequired ? "olreadia-compose-choice is-active" : "olreadia-compose-choice"}
                  >
                    Optional
                  </Button>
                </Space>
              </div>

              <div>
                <Text className="olreadia-compose-question-label">Metric</Text>
                <Space wrap className="olreadia-compose-metrics">
                  {METRIC_OPTIONS.map((metric) => (
                    <Button
                      key={metric}
                      size="small"
                      className={composeAnswerMetric.toLowerCase() === metric ? "is-active" : ""}
                      onClick={() => setComposeAnswerMetric((current) => current.toLowerCase() === metric ? "" : metric)}
                    >
                      {metric}
                    </Button>
                  ))}
                </Space>
                <Input
                  value={composeAnswerMetric}
                  onChange={(event) => setComposeAnswerMetric(event.target.value)}
                  placeholder="Custom metric"
                  style={{ marginTop: 8 }}
                />
              </div>

              {isChoiceAnswerType(composeAnswerType) ? (
                <div className="olreadia-compose-options">
                  <Text className="olreadia-compose-question-label">Answer Options</Text>
                  {composeAnswerOptions.length ? (
                    <Space wrap className="olreadia-question-option-chips">
                      {composeAnswerOptions.map((option) => (
                        <Tag
                          key={option}
                          closable
                          onClose={() => setComposeAnswerOptions((current) => current.filter((item) => item !== option))}
                        >
                          {option}
                        </Tag>
                      ))}
                    </Space>
                  ) : null}
                  <div className="olreadia-compose-option-row">
                    <Input.TextArea
                      autoSize={{ minRows: 2, maxRows: 4 }}
                      value={composeOptionDraft}
                      onChange={(event) => setComposeOptionDraft(event.target.value)}
                      placeholder="Add options, one per line"
                    />
                    <Button type="primary" icon={<PlusOutlined />} onClick={addComposeOptions}>
                      Add
                    </Button>
                  </div>
                </div>
              ) : null}
            </Space>
          </Modal>
        </main>

        <AiJobDescriptionModal
          open={aiModalOpen}
          prompt={aiPrompt}
          onPromptChange={setAiPrompt}
          generating={aiGenerating}
          draft={aiDraft}
          error={aiError}
          onGenerate={generateAiDescription}
          onApply={applyAiDraft}
          onClose={closeAiGenerator}
          usage={aiUsage}
        />

        <SidePromotionSection className="olreadia-flow-promotions" />
      </div>
    </div>
  );
}
